import React from "react";
import { buildSoldNestedTree, collectSoldLinesFromOrders, formatVariantGuillemets } from "../../helpers/ordersSoldNestedList";

function formatStock(v) {
    if (v == null || Number.isNaN(v)) return "—";
    return String(v);
}

function SoldCount({ children }) {
    return <strong className="orders-nested-sold-num">{children}</strong>;
}

function stockNumClass(stock) {
    const n = Number(stock);
    if (!Number.isFinite(n) || n >= 0) return "orders-nested-stock-num";
    return "orders-nested-stock-num orders-nested-stock-num--negative";
}

/** Один рядок як у скріншоті Карго: варіант | прод. | число | зал. | число (спільна сітка товару вирівнює стовпці). */
function StatsGridCells({ label, sold, stock, labelCellClassName }) {
    const labelClass = [
        "orders-nested-stats-cell",
        "orders-nested-stats-cell--label",
        labelCellClassName,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <>
            <div className={labelClass}>{label}</div>
            <span className="orders-nested-stats-hdr">прод.</span>
            <div className="orders-nested-stats-num-cell">
                <SoldCount>{sold}</SoldCount>
            </div>
            <span className="orders-nested-stats-hdr">зал.</span>
            <div className="orders-nested-stats-num-cell">
                <strong className={stockNumClass(stock)}>{formatStock(stock)}</strong>
            </div>
        </>
    );
}

function ColorAggOneLine({ block, hideSizeLabel }) {
    const row = block.rows?.[0];
    if (!row) return null;

    let sizeSuffix = null;
    if (row.mergedSizes) {
        sizeSuffix = <span className="orders-nested-meta"> (Усі розміри)</span>;
    } else if (!hideSizeLabel && row.size && row.size !== "—") {
        sizeSuffix = <span className="orders-nested-meta"> ({row.size})</span>;
    }

    const label = (
        <>
            <strong className="orders-nested-color-sold-name">{block.groupKey}</strong>
            {sizeSuffix}
        </>
    );

    return (
        <div className="orders-nested-color-sold-line">
            <div className="orders-nested-stats-grid orders-nested-stats-grid--single">
                <StatsGridCells label={label} sold={row.sold} stock={row.stock} />
            </div>
        </div>
    );
}

function SizeAggOneLine({ block }) {
    const row = block.rows?.[0];
    const stock = row?.stock ?? null;
    const label = <strong className="orders-nested-size-sold-label">{block.groupKey}</strong>;

    return (
        <div className="orders-nested-size-sold-line">
            <div className="orders-nested-stats-grid orders-nested-stats-grid--single">
                <StatsGridCells label={label} sold={block.totalSold} stock={stock} />
            </div>
        </div>
    );
}

function detailLineLabel(row, hideSizeLabel) {
    if (row.mergedSizes) {
        return <strong>Усі розміри</strong>;
    }
    const quoted = formatVariantGuillemets(row.variantLabel);
    const showSizeBold =
        !quoted && !hideSizeLabel && row.size && row.size !== "—";

    if (quoted) {
        return <strong>{quoted}</strong>;
    }
    if (showSizeBold) {
        return <strong>{row.size}</strong>;
    }
    return null;
}

function DetailLineCells({ row, hideSizeLabel }) {
    return (
        <StatsGridCells
            labelCellClassName="orders-nested-stats-cell--label-indented"
            label={detailLineLabel(row, hideSizeLabel)}
            sold={row.sold}
            stock={row.stock}
        />
    );
}

/**
 * Багаторівневий список проданих позицій (ті самі замовлення, що й у зведенні).
 * Агрегація лише в межах одного товару: за кольором (усі розміри разом) або за розміром (усі кольори разом).
 *
 * @param {'none'|'color'|'size'} aggregationMode
 * @param {'sold'|'alphabet'} sortOrder
 */
export default function OrdersSoldNestedList({ orders, aggregationMode, sortOrder = "sold" }) {
    const lines = collectSoldLinesFromOrders(orders || []);
    const tree = buildSoldNestedTree(lines, aggregationMode, sortOrder);

    if (!tree.length) {
        return (
            <p className="orders-nested-sold-empty text-muted px-3">Немає проданих позицій для обраного періоду.</p>
        );
    }

    return (
        <div className="orders-nested-sold-list">
            {tree.map((group, gi) => (
                <article key={`${aggregationMode}-${sortOrder}-${group.groupLabel}-${gi}`} className="orders-nested-group">
                    <header className="orders-nested-l1">
                        <strong>{group.groupLabel}</strong>
                        <span className="orders-nested-l1-meta">
                            {" "}(прод. <SoldCount>{group.totalSold}</SoldCount>)
                        </span>
                    </header>

                    {aggregationMode === "size" &&
                        (group.sizeBlocks || []).map((block) => (
                            <SizeAggOneLine
                                key={`${group.groupLabel}-${block.groupKey}`}
                                block={block}
                            />
                        ))}

                    {aggregationMode === "color" &&
                        (group.colorBlocks || []).map((block) => (
                            <ColorAggOneLine
                                key={`${group.groupLabel}-${block.groupKey}`}
                                block={block}
                                hideSizeLabel={group.hideSizeLabel}
                            />
                        ))}

                    {aggregationMode === "none" && (
                        <div className="orders-nested-stats-grid orders-nested-stats-grid--product">
                            {(group.colorBlocks || []).map((block, bi) => (
                                <React.Fragment key={`${group.groupLabel}-${block.groupKey}-${bi}`}>
                                    <div className="orders-nested-stats-color-head orders-nested-color">
                                        {block.groupKey}
                                    </div>
                                    {(block.rows || []).map((row, ri) => (
                                        <React.Fragment
                                            key={`${block.groupKey}-${row.typeLabel}-${row.variantLabel}-${row.size}-${row.sku}-${ri}`}
                                        >
                                            <DetailLineCells row={row} hideSizeLabel={group.hideSizeLabel} />
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </article>
            ))}
        </div>
    );
}
